import { useState, useEffect } from 'react';
import { TListingClient } from 'types/TListing';
import { TFeedItem } from 'types/TFeeds';
import { TAdmarktItem } from 'types/TAdmarkt';
import fetchAPI from '../../../utils/fetchAPI';
import formatQueryString from '../../../utils/formatQueryString';
import getSimilarItems from '../repositories/getSimilarItems';

type TUseAdmarktSimilarItemsParams = {
  itemId: TListingClient['itemId'];
  sellerId: TListingClient['seller']['id'];
  category: TListingClient['category'];
  isSimilarItemsFeedGroupBEnabled?: boolean;
  isSimilarItemsFeedGroupCEnabled?: boolean;
};

const FEED_ITEMS_LIMIT_GROUP_B = 12;
const FEED_ITEMS_LIMIT_GROUP_C = 24;

const getFeedItemsLimit = (isGroupBEnabled?: boolean, isGroupCEnabled?: boolean): number => {
  if (!isGroupBEnabled && !isGroupCEnabled) return 0;
  return isGroupCEnabled ? FEED_ITEMS_LIMIT_GROUP_C : FEED_ITEMS_LIMIT_GROUP_B;
};

const useAdmarktSimilarItems = ({
  itemId,
  sellerId,
  category,
  isSimilarItemsFeedGroupBEnabled,
  isSimilarItemsFeedGroupCEnabled,
}: TUseAdmarktSimilarItemsParams) => {
  const [items, setItems] = useState<(TFeedItem | TAdmarktItem)[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isFeedExperimentEnabled = isSimilarItemsFeedGroupBEnabled || isSimilarItemsFeedGroupCEnabled;
  const limit = getFeedItemsLimit(isSimilarItemsFeedGroupBEnabled, isSimilarItemsFeedGroupCEnabled);

  useEffect(() => {
    let isSubscribed = true;

    if (isFeedExperimentEnabled) {
      const fetchItems = async () => {
        try {
          setIsLoading(true);
          const response = await fetchAPI({
            url: `/v/api/feed-items?${formatQueryString({ itemId, limit })}`,
            preloadCompatible: true,
          });
          if (isSubscribed) {
            // Only update state if component is mounted
            setItems(response);
          }
        } catch (error) {
          if (isSubscribed) {
            setItems([]);
          }
        } finally {
          if (isSubscribed) {
            setIsLoading(false);
          }
        }
      };

      fetchItems();
    } else {
      (async () => {
        setIsLoading(true);
        const response = await getSimilarItems({ sellerId, itemId, l2CategoryId: category?.id });
        if (isSubscribed) {
          setItems(response);
          setIsLoading(false);
        }
      })();
    }

    return () => {
      isSubscribed = false;
    };
  }, [itemId, sellerId, category?.id, isFeedExperimentEnabled, limit]);

  return { items, isLoading };
};

export default useAdmarktSimilarItems;
